import { FormEvent, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Autocomplete, FormControl } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { addingRobotAction, addingRobotException, deleteActionRobot, deleteRobotException } from "../../../store/ses/sesRobotSlice";
import {
	addingDataElemAction,
	addingEndpointAction,
	addingEndpointEntryAction,
	addException,
	deleteActionDataElem,
	deleteActionEndpoint,
	deleteActionEndpointEntry,
	deleteException,
} from "@/store/ses/sesItemConfigSlice";
import { selectChannelList } from "../../../store/langSlice";
import useTranslate from "../../../hooks/useTranslate";
import ModalFormWindow from "../../../HOC/ModalFormWindow/ModalFormWindow";
import NoOptionText from "../../../ui/NoOptionText/NoOptionText";
import { IFormCopyAndMoveActionProps } from "./FormCopyAndMoveAction.props";
import { CustomButton, CustomTextField } from "@/ui";
import { RenderOption } from "@/ui";

const FormCopyAndMoveAction = ({ showModal, setShowModal, action, sourceChannel, sourceIdx, actionFor, changeFlgHandler }: IFormCopyAndMoveActionProps): JSX.Element => {
	const [selectChannel, setSelectChannel] = useState<string | null>(null); // канал

	const dispatch = useAppDispatch();
	const channelList = useAppSelector(selectChannelList); // store - список каналов

	const translate = useTranslate(); // hook для перевода текста

	// обработчик копирования/перемещения действия
	const submitHandler = (e: FormEvent<HTMLFormElement>): void => {
		e.preventDefault();
		if (!selectChannel) return;
		// для копии новый actionId
		const dataToCopy = { channel: selectChannel, action: showModal.type === "copy" ? { ...action, actionId: uuidv4() } : action };
		// добавление действия
		if (actionFor.for === "robotException") {
			dispatch(addingRobotException({ exception: actionFor.exception, ...dataToCopy })); // исключения робота
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "dataException") {
			dispatch(addException({ targetObject: "dataElement", exception: actionFor.exception, ...dataToCopy })); // исключения ЭД
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointException") {
			dispatch(addException({ targetObject: "endpoint", exception: actionFor.exception, ...dataToCopy })); // исключения КТ
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointEntry") {
			dispatch(addingEndpointEntryAction({ ...dataToCopy, withTimer: actionFor.withTimer })); // входа КТ
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else {
			actionFor.for === "robotEvent" && dispatch(addingRobotAction({ actionEvent: actionFor.event, ...dataToCopy })); // робота
			actionFor.for === "dataElement" && dispatch(addingDataElemAction({ actionEvent: actionFor.event, ...dataToCopy })); // ЭД
			actionFor.for === "endpoint" && dispatch(addingEndpointAction({ ...dataToCopy })); // КТ
			changeFlgHandler("actions"); // ставим флаг о несохраненных данных
		}

		// удаление действия
		if (showModal.type === "move") {
			const dataToMove = { channel: sourceChannel, idx: sourceIdx };
			if (actionFor.for === "robotException") {
				dispatch(deleteRobotException({ exception: actionFor.exception, ...dataToMove })); // исключения робота
			} else if (actionFor.for === "dataException") {
				dispatch(deleteException({ targetObject: "dataElement", exception: actionFor.exception, ...dataToMove })); // исключения ЭД
			} else if (actionFor.for === "endpointException") {
				dispatch(deleteException({ targetObject: "endpoint", exception: actionFor.exception, ...dataToMove })); // исключения КТ
			} else if (actionFor.for === "endpointEntry") {
				dispatch(deleteActionEndpointEntry({ ...dataToMove })); // входа КТ
			} else {
				actionFor.for === "robotEvent" && dispatch(deleteActionRobot({ actionEvent: actionFor.event, ...dataToMove })); // робота
				actionFor.for === "dataElement" && dispatch(deleteActionDataElem({ actionEvent: actionFor.event, ...dataToMove })); // ЭД
				actionFor.for === "endpoint" && dispatch(deleteActionEndpoint({ ...dataToMove })); // КТ
			}
		}

		formCloseHandler(); // закрываем форму
	};

	// обработчик закрытия формы
	const formCloseHandler = (): void => {
		setShowModal((prev) => ({ ...prev, isShown: false }));
	};

	return (
		<ModalFormWindow showModal={showModal.isShown} headerTitle={showModal.type === "copy" ? "title_copyAction" : "title_movingAction"} close={formCloseHandler}>
			<form onSubmit={submitHandler}>
				{/* канал */}
				<FormControl fullWidth margin="dense">
					<Autocomplete
						autoHighlight
						options={Array.from(new Set(["default", sourceChannel].concat(channelList.data.map((channel) => channel.name)))).filter((channelName) => {
							if (showModal.type === "move" && channelName === sourceChannel) return false;
							else if (
								showModal.type === "copy" &&
								(sourceChannel === "voice" || sourceChannel === "web") &&
								channelName === sourceChannel &&
								actionFor.for === "robotEvent" &&
								(actionFor.event === "answer delay" || actionFor.event === "interrupt")
							)
								return false;
							else return true;
						})}
						value={selectChannel}
						onChange={(_, value) => setSelectChannel(value)}
						noOptionsText={<NoOptionText />}
						renderInput={(params) => <CustomTextField {...params} required autoFocus label={translate("select_channel")} />}
						renderOption={(props, option) => {
							return (
								<RenderOption {...props} key={option}>
									{option}
								</RenderOption>
							);
						}}
					/>
				</FormControl>

				<FormControl fullWidth margin="dense">
					<CustomButton type="submit">{translate(showModal.type === "copy" ? "button_copy" : "button_move")}</CustomButton>
				</FormControl>
			</form>
		</ModalFormWindow>
	);
};

export default FormCopyAndMoveAction;
